import React, { useState, useCallback, useEffect } from "react";
import portariaService from "../../services/portariaService";
import PortalNotificacoesForm from "../portal/PortalNotificacoesForm";
import {
  HistoricoAviso,
  AbasAvisos,
  AvisoCardColapsavel,
  filtrarAvisos,
  contarAvisos,
} from "./AvisosShared";

const fmtDT = (d) => {
  try {
    return new Date(d).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
};

const SITUACAO_STYLE = {
  autorizado: { bg: "#DCFCE7", color: "#166534", label: "Autorizado" },
  bloqueado: { bg: "#FEE2E2", color: "#991B1B", label: "Bloqueado" },
  sem_autorizacao: { bg: "#FEF9C3", color: "#713F12", label: "Sem autorização" },
};
const CATEGORIAS = ["Manutencao", "Limpeza", "Entrega", "Saude", "Outro"];
const CATEGORIA_LABEL = {
  Manutencao: "Manutenção",
  Limpeza: "Limpeza",
  Entrega: "Entrega",
  Saude: "Saúde",
  Outro: "Outro",
};
const CATEGORIA_STYLE = {
  Manutencao: { bg: "#EFF6FF", color: "#1D4ED8" },
  Limpeza: { bg: "#F0FDF4", color: "#166534" },
  Entrega: { bg: "#FEF9C3", color: "#854D0E" },
  Saude: { bg: "#FCE7F3", color: "#9D174D" },
  Outro: { bg: "#F1F5F9", color:"var(--gray-600)" },
};

const fmtCategoria = (c) => CATEGORIA_LABEL[c] || c || "Outro";

const fmtUnid = (u) => `${u.unidade}${u.bloco ? ` / Bl. ${u.bloco}` : ""}`;

function BadgeCategoria({ categoria }) {
  const cat = categoria || "Outro";
  const st = CATEGORIA_STYLE[cat] || CATEGORIA_STYLE.Outro;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4,
      background: st.bg, color: st.color, whiteSpace: "nowrap",
    }}>
      {fmtCategoria(cat)}
    </span>
  );
}

function TelefoneLink({ telefone }) {
  const txt = (telefone || "").trim();
  if (!txt) {
    return <span style={{ fontSize: 12, color:"var(--gray-400)" }}>Sem telefone</span>;
  }
  const digits = txt.replace(/\D/g, "");
  return (
    <a
      href={digits ? `tel:${digits}` : undefined}
      style={{
        fontSize: 13, fontWeight: 600, color: "#1B3A6B", textDecoration: "none",
        display: "inline-flex", alignItems: "center", gap: 4,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <i className="bi bi-telephone-fill" style={{ fontSize: 12 }}/>
      {txt}
    </a>
  );
}

function PrestadorCardMeta({ p, situacao }) {
  const st = situacao ? (SITUACAO_STYLE[situacao] || SITUACAO_STYLE.sem_autorizacao) : null;
  const secundario = [p.documento, p.empresa].filter(Boolean).join(" · ");
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>{p.nmPrestador}</div>
      <div style={{
        display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px 12px", marginBottom: 4,
      }}>
        <BadgeCategoria categoria={p.categoria} />
        <TelefoneLink telefone={p.telefone} />
        {p.status === "Bloqueado" && (
          <span style={{ fontSize: 10, fontWeight: 700, color: "#991B1B" }}>(bloqueado)</span>
        )}
      </div>
      {secundario ? (
        <div style={{ fontSize: 12, color:"var(--gray-500)" }}>{secundario}</div>
      ) : null}
      {st && (
        <span style={{
          fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4,
          background: st.bg, color: st.color, marginTop: 6, display: "inline-block",
        }}>{st.label}</span>
      )}
    </div>
  );
}

export function NotificacoesPortariaPanel({ usuario }) {
  const storageKey = usuario?.id ? `portaria_push_onboarding_${usuario.id}` : null;
  const [showOnboard, setShowOnboard] = useState(false);

  useEffect(() => {
    if (!storageKey || !usuario?.primeiroAcesso) return;
    try {
      if (!localStorage.getItem(storageKey)) setShowOnboard(true);
    } catch { /* ignore */ }
  }, [storageKey, usuario?.primeiroAcesso]);

  const dismissOnboard = () => {
    if (storageKey) {
      try { localStorage.setItem(storageKey, "1"); } catch { /* ignore */ }
    }
    setShowOnboard(false);
  };

  return (
    <div>
      {showOnboard && (
        <div style={{
          padding: 14, marginBottom: 16, background:"var(--bg-highlight)",
          border: "1px solid #BFDBFE", borderRadius: 10, color: "#1E40AF", fontSize: 13,
        }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            <i className="bi bi-bell me-2"/>Bem-vindo à portaria
          </div>
          <p style={{ margin: "0 0 10px" }}>
            Ative as notificações push para receber avisos urgentes dos condôminos em tempo real.
          </p>
          <button type="button" className="btn btn-primary btn-sm" onClick={dismissOnboard}>
            Entendi
          </button>
        </div>
      )}
      <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Notificações push</h3>
      <PortalNotificacoesForm variant="portaria" />
    </div>
  );
}

export function AvisosPortariaPanel({ idCondo, unidades = [], notify, onAtualizarDashboard }) {
  const [avisos, setAvisos] = useState([]);
  const [abaLista, setAbaLista] = useState("abertos");
  const [filtroUnidade, setFiltroUnidade] = useState("");
  const [loading, setLoading] = useState(false);
  const [resposta, setResposta] = useState({});
  const [expandidos, setExpandidos] = useState({});

  const carregar = useCallback(async () => {
    if (!idCondo) return;
    setLoading(true);
    try {
      const r = await portariaService.avisos({ idCondominio: idCondo });
      setAvisos(r.data || []);
    } catch (e) { notify("error", e.message); }
    setLoading(false);
  }, [idCondo, notify]);

  useEffect(() => { carregar(); }, [carregar]);

  const toggleCard = (id) => {
    setExpandidos((e) => ({ ...e, [id]: !e[id] }));
  };

  const labelUnidade = (a) =>
    `Unidade ${a.nmUnidade}${a.bloco ? ` / Bl. ${a.bloco}` : ""}`;

  const { abertos: qtdAbertos, resolvidos: qtdResolvidos } = contarAvisos(avisos, filtroUnidade);
  const listaFiltrada = filtrarAvisos(avisos, { aba: abaLista, idUnidade: filtroUnidade });

  const limparRespostaLocal = (id) => {
    setResposta((r) => {
      const next = { ...r };
      delete next[id];
      return next;
    });
  };

  const enviarResposta = async (id) => {
    const txt = (resposta[id] || "").trim();
    if (!txt) { notify("warning", "Digite a resposta para o condômino."); return; }
    try {
      await portariaService.atualizarAviso(id, { respostaPortaria: txt });
      limparRespostaLocal(id);
      notify("success", "Resposta enviada ao condômino.");
      await carregar();
    } catch (e) { notify("error", e.message); }
  };

  const atualizar = async (id, status) => {
    const txt = (resposta[id] || "").trim();
    try {
      await portariaService.atualizarAviso(id, {
        status,
        ...(txt ? { respostaPortaria: txt } : {}),
      });
      limparRespostaLocal(id);
      notify("success", status === "Resolvido" ? "Aviso resolvido." : "Atualizado.");
      await carregar();
      onAtualizarDashboard?.();
    } catch (e) { notify("error", e.message); }
  };

  return (
    <div>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: 14, flexWrap: "wrap", gap: 8,
      }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Avisos dos condôminos</h3>
        <select className="form-select form-select-sm" style={{ width: "auto" }}
          value={filtroUnidade} onChange={(e) => setFiltroUnidade(e.target.value)}>
          <option value="">Todas as unidades</option>
          {unidades.map((u) => (
            <option key={u.id} value={u.id}>{fmtUnid(u)}</option>
          ))}
        </select>
      </div>

            <AbasAvisos
        aba={abaLista}
        onAba={setAbaLista}
        qtdAbertos={qtdAbertos}
        qtdResolvidos={qtdResolvidos}
      />

      {loading ? (
        <div className="text-center py-4 text-muted"><span className="spinner-border spinner-border-sm"/></div>
      ) : avisos.length === 0 ? (
        <div className="card p-4 text-center text-muted" style={{ fontSize: 13 }}>
          Nenhum aviso recebido ainda.
        </div>
      ) : listaFiltrada.length === 0 ? (
        <div className="card p-4 text-center text-muted" style={{ fontSize: 13 }}>
          {abaLista === "abertos"
            ? "Nenhum aviso em aberto."
            : "Nenhum aviso resolvido."}
          {filtroUnidade ? " Tente outra unidade." : ""}
        </div>
      ) : (
        <div>
          {listaFiltrada.map((a) => (
            <AvisoCardColapsavel
              key={a.id}
              aviso={a}
              aberto={!!expandidos[a.id]}
              onToggle={() => toggleCard(a.id)}
              labelUnidade={labelUnidade(a)}
            >
              <HistoricoAviso historico={a.historico} labelCondomino="Condômino" />
              {a.status !== "Resolvido" && (
                <>
                  <div style={{ fontSize: 11, color:"var(--gray-500)", marginBottom: 6 }}>
                    Interação aberta — você e o condômino podem trocar mensagens até resolver.
                  </div>
                  <textarea className="form-control form-control-sm mb-2" rows={2}
                    placeholder="Nova resposta ao condômino…"
                    value={resposta[a.id] || ""}
                    onChange={(e) => setResposta((r) => ({ ...r, [a.id]: e.target.value }))}/>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button type="button" className="btn btn-outline-secondary btn-sm"
                      onClick={() => enviarResposta(a.id)}>
                      Enviar resposta
                    </button>
                    {a.status === "Novo" && (
                      <button type="button" className="btn btn-outline-primary btn-sm"
                        onClick={() => atualizar(a.id, "Visto")}>
                        Marcar como visto
                      </button>
                    )}
                    <button type="button" className="btn btn-success btn-sm"
                      onClick={() => atualizar(a.id, "Resolvido")}>
                      Resolver
                    </button>
                  </div>
                </>
              )}
            </AvisoCardColapsavel>
          ))}
        </div>
      )}
    </div>
  );
}

const EMPTY_PREST = {
  nmPrestador: "", documento: "", telefone: "", empresa: "", categoria: "Outro", observacao: "",
};

export function PrestadoresPortariaPanel({
  idCondo, unidades = [], notify, onRegistrarEntrada,
}) {
  const [lista, setLista] = useState([]);
  const [busca, setBusca] = useState("");
  const [resultados, setResultados] = useState([]);
  const [modo, setModo] = useState("Alertar");
  const [idUnidadeBusca, setIdUnidadeBusca] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editPrest, setEditPrest] = useState(null);
  const [form, setForm] = useState(EMPTY_PREST);
  const [saving, setSaving] = useState(false);

  const carregarLista = useCallback(async () => {
    if (!idCondo) return;
    try {
      const r = await portariaService.prestadores({ idCondominio: idCondo, search: busca });
      setLista(r.data || []);
    } catch (e) { notify("error", e.message); }
  }, [idCondo, busca, notify]);

  useEffect(() => { carregarLista(); }, [carregarLista]);

  const buscar = async () => {
    if (!busca.trim()) { notify("warning", "Digite nome, documento ou empresa."); return; }
    setLoading(true);
    try {
      const r = await portariaService.buscarPrestadores({
        idCondominio: idCondo,
        q: busca.trim(),
        idUnidade: idUnidadeBusca || undefined,
      });
      setResultados(r.data || []);
      setModo(r.modoPrestadorSemAutorizacao || "Alertar");
    } catch (e) { notify("error", e.message); }
    setLoading(false);
  };

  const abrirEditar = (p) => {
    setEditPrest(p);
    setForm({
      nmPrestador: p.nmPrestador || "",
      documento: p.documento || "",
      telefone: p.telefone || "",
      empresa: p.empresa || "",
      categoria: p.categoria || "Outro",
      observacao: p.observacao || "",
    });
    setShowForm(false);
  };

  const salvarPrestador = async (e) => {
    e.preventDefault();
    if (!form.nmPrestador.trim()) { notify("warning", "Nome é obrigatório."); return; }
    setSaving(true);
    try {
      if (editPrest?.id) {
        await portariaService.atualizarPrestador(editPrest.id, form);
        notify("success", "Prestador atualizado.");
        setEditPrest(null);
      } else {
        await portariaService.criarPrestador({ idCondominio: idCondo, ...form });
        notify("success", "Prestador cadastrado.");
        setShowForm(false);
      }
      setForm(EMPTY_PREST);
      carregarLista();
    } catch (err) { notify("error", err.message); }
    setSaving(false);
  };

  const bloquear = async (p) => {
    const motivo = window.prompt("Motivo do bloqueio (opcional):") ?? "";
    try {
      await portariaService.atualizarPrestador(p.id, { status: "Bloqueado", motivoBloqueio: motivo });
      notify("success", "Prestador bloqueado.");
      carregarLista();
      if (resultados.some((x) => x.id === p.id)) buscar();
    } catch (e) { notify("error", e.message); }
  };

  const desbloquear = async (p) => {
    try {
      await portariaService.atualizarPrestador(p.id, { status: "Ativo" });
      notify("success", "Prestador reativado.");
      carregarLista();
      if (resultados.some((x) => x.id === p.id)) buscar();
    } catch (e) { notify("error", e.message); }
  };

  const tentarEntrada = (p, situacao) => {
    if (p.status === "Bloqueado" || situacao === "bloqueado") {
      notify("error", "Prestador bloqueado — entrada não permitida.");
      return;
    }
    if (situacao === "sem_autorizacao" && modo === "Bloquear") {
      notify("error", "Sem autorização da unidade — condomínio configurado para bloquear.");
      return;
    }
    if (situacao === "sem_autorizacao" && modo === "Alertar") {
      notify("warning", "Prestador sem autorização vigente — confirme com o morador.");
    }
    onRegistrarEntrada?.({
      nmVisitante: p.nmPrestador,
      tpVisita: "Prestador",
      documento: p.documento || "",
      empresa: p.empresa || "",
      idUnidade: idUnidadeBusca || "",
    });
  };

  return (
    <div>
      <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Prestadores de serviço</h3>

      <div className="card" style={{ padding: 14, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Busca rápida na entrada</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
          <input className="form-control form-control-sm" style={{ flex: 1, minWidth: 160 }}
            placeholder="Nome, CPF/CNPJ ou empresa…"
            value={busca} onChange={(e) => setBusca(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && buscar()}/>
          <select className="form-select form-select-sm" style={{ width: 140 }}
            value={idUnidadeBusca} onChange={(e) => setIdUnidadeBusca(e.target.value)}>
            <option value="">Unidade (opc.)</option>
            {unidades.map((u) => (
              <option key={u.id} value={u.id}>{fmtUnid(u)}</option>
            ))}
          </select>
          <button type="button" className="btn btn-primary btn-sm" disabled={loading} onClick={buscar}>
            {loading ? <span className="spinner-border spinner-border-sm"/> : <><i className="bi bi-search me-1"/>Buscar</>}
          </button>
        </div>
        <div style={{ fontSize: 11, color:"var(--gray-500)" }}>
          Regra sem autorização: <strong>{modo === "Bloquear" ? "Bloquear entrada" : modo === "Permitir" ? "Permitir" : "Alertar portaria"}</strong>
        </div>
        {resultados.length > 0 && (
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
            {resultados.map((p) => (
                <div key={p.id} style={{
                  padding: 10, border:"1px solid var(--border-color)", borderRadius: 8,
                  display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8,
                }}>
                  <PrestadorCardMeta p={p} situacao={p.situacao} />
                  <div style={{ display: "flex", gap: 6 }}>
                    <button type="button" className="btn btn-primary btn-sm"
                      onClick={() => tentarEntrada(p, p.situacao)}>
                      Registrar entrada
                    </button>
                    {p.status === "Bloqueado" ? (
                      <button type="button" className="btn btn-outline-success btn-sm" onClick={() => desbloquear(p)}>
                        Reativar
                      </button>
                    ) : (
                      <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => bloquear(p)}>
                        Bloquear
                      </button>
                    )}
                  </div>
                </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Cadastro no condomínio</span>
        <button type="button" className="btn btn-outline-primary btn-sm"
          onClick={() => setShowForm((s) => !s)}>
          <i className={`bi bi-${showForm ? "dash" : "plus"}-lg me-1`}/>
          {showForm ? "Cancelar" : "Novo prestador"}
        </button>
      </div>

      {(showForm || editPrest) && (
        <form className="card p-3 mb-3" onSubmit={salvarPrestador} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>
            {editPrest ? "Editar prestador" : "Novo prestador"}
          </div>
          <input className="form-control form-control-sm" required placeholder="Nome *"
            value={form.nmPrestador} onChange={(e) => setForm((f) => ({ ...f, nmPrestador: e.target.value }))}/>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input className="form-control form-control-sm" style={{ flex: 1 }} placeholder="CPF/CNPJ"
              value={form.documento} onChange={(e) => setForm((f) => ({ ...f, documento: e.target.value }))}/>
            <input className="form-control form-control-sm" style={{ flex: 1 }} placeholder="Telefone"
              value={form.telefone} onChange={(e) => setForm((f) => ({ ...f, telefone: e.target.value }))}/>
          </div>
          <input className="form-control form-control-sm" placeholder="Empresa"
            value={form.empresa} onChange={(e) => setForm((f) => ({ ...f, empresa: e.target.value }))}/>
          <select className="form-select form-select-sm" value={form.categoria}
            onChange={(e) => setForm((f) => ({ ...f, categoria: e.target.value }))}>
            {CATEGORIAS.map((c) => <option key={c} value={c}>{fmtCategoria(c)}</option>)}
          </select>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
              {saving ? "Salvando…" : "Salvar"}
            </button>
            {editPrest && (
              <button type="button" className="btn btn-outline-secondary btn-sm"
                onClick={() => { setEditPrest(null); setForm(EMPTY_PREST); }}>
                Cancelar
              </button>
            )}
          </div>
        </form>
      )}

      {lista.length === 0 ? (
        <div className="text-muted" style={{ fontSize: 13 }}>Nenhum prestador cadastrado.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {lista.slice(0, 50).map((p) => (
            <div key={p.id} style={{
              padding: "10px 12px", border:"1px solid var(--border-color)", borderRadius: 8, fontSize: 13,
              display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8,
            }}>
              <PrestadorCardMeta p={p} />
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" className="btn btn-outline-primary btn-sm"
                  onClick={() => abrirEditar(p)}>
                  Editar
                </button>
                <button type="button" className="btn btn-link btn-sm p-0"
                  onClick={() => { setBusca(p.nmPrestador); setResultados([]); }}>
                  Buscar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
